import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Form from '@cloudscape-design/components/form';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Link from '@cloudscape-design/components/link';
import SegmentedControl from '@cloudscape-design/components/segmented-control';
import SpaceBetween from '@cloudscape-design/components/space-between';
import { useAuth } from '@vextura/shell';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Tenant sign-up screen. First user of a company registers here, picks the
 * tenant slug, and lands signed-in as the tenant root. Subsequent members
 * are invited from the Users section by the root user, never through here.
 *
 * Contract: mirrors vex-auth SignUp (POST /auth/signup). Tenant slug rules
 * match the server regex ^[a-z][a-z0-9-]{2,31}$ so the "Create tenant"
 * button doesn't submit a payload the server will 400.
 */
const TENANT_SLUG_RE = /^[a-z][a-z0-9-]{2,31}$/;

/**
 * Plans that trigger enterprise-style lead capture instead of self-serve.
 * These flows collect company info + intent + hide the password fields so
 * ops can reach out within 24h. The self-serve flow (cloud free/team,
 * on-prem starter for a small team) uses the full form.
 */
const LEAD_CAPTURE_PLANS = new Set(['growth', 'scale', 'enterprise']);

/** Human labels for the deploy/plan combinations we surface at the top of the form. */
const PLAN_LABELS: Record<string, string> = {
  free: 'Cloud · Free',
  team: 'Cloud · Team',
  enterprise: 'Cloud · Enterprise',
  starter: 'On-premises · Starter',
  growth: 'On-premises · Growth',
  scale: 'On-premises · Scale',
};

export function SignUpScreen(): ReactNode {
  const { signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Read deploy + plan from the URL. Landing CTAs drop us here with both
  // set; direct /signup visits show the cloud/team default which is our
  // most common self-serve target.
  const initialDeploy = searchParams.get('deploy') === 'onprem' ? 'onprem' : 'cloud';
  const initialPlan = searchParams.get('plan') ?? (initialDeploy === 'cloud' ? 'free' : 'starter');

  const [deploy, setDeploy] = useState<'cloud' | 'onprem'>(initialDeploy);
  const [plan, setPlan] = useState<string>(initialPlan);
  const isLeadCapture = useMemo(() => LEAD_CAPTURE_PLANS.has(plan), [plan]);

  const [companyName, setCompanyName] = useState('');
  const [tenant, setTenant] = useState('');
  const [tenantTouched, setTenantTouched] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If the tenant field hasn't been edited yet, auto-suggest a slug from
  // the company name. Same regex the server uses (drop non-[a-z0-9-], leading
  // digit becomes t-prefixed so the pattern's leading-letter rule holds).
  useEffect(() => {
    if (tenantTouched) return;
    if (!companyName) {
      setTenant('');
      return;
    }
    let s = companyName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    if (s && !/^[a-z]/.test(s)) s = 't-' + s;
    setTenant(s.slice(0, 32));
  }, [companyName, tenantTouched]);

  // If we're already signed in (e.g. user hit /signup manually), bounce home.
  useEffect(() => {
    if (user) navigate('/', { replace: true });
  }, [user, navigate]);

  const tenantValid = TENANT_SLUG_RE.test(tenant);
  const passwordValid = password.length >= 8;
  const passwordsMatch = password === confirmPassword;
  const canSubmit =
    companyName.trim() !== '' &&
    tenantValid &&
    username.trim() !== '' &&
    email.trim() !== '' &&
    (isLeadCapture || (passwordValid && passwordsMatch));

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      if (isLeadCapture) {
        // Enterprise / Growth / Scale — no self-serve signup, just capture
        // the lead and surface a "we'll reach out" message. Backend hook
        // will be wired in a follow-up; for now we simulate the ack so the
        // UX is complete and the operator can verify the flow.
        // TODO(SIGNUP-01/xx): POST /auth/lead {companyName, tenant, email,
        //   deploy, plan} once the vex-auth endpoint lands.
        setSubmitting(false);
        navigate('/signup/thanks', { replace: true });
        return;
      }
      await signUp({
        companyName: companyName.trim(),
        tenant,
        username: username.trim(),
        email: email.trim(),
        password,
        displayName: displayName.trim() || username.trim(),
      });
      // AuthProvider state has updated → RequireAuth will let / through.
      navigate('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  const heroDescription = isLeadCapture
    ? "Tell us about your company and we'll be in touch within 24 hours to walk you through installation."
    : 'Register a new company tenant. The first user becomes the tenant root and can invite the rest of the team from Users.';

  return (
    <Box padding="xxxl">
      <div style={{ maxWidth: 520, margin: '80px auto 0' }}>
        <Container
          header={
            <Header variant="h1" description={heroDescription}>
              {isLeadCapture ? 'Talk to us' : 'Create your Vextura tenant'}
            </Header>
          }
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void onSubmit();
            }}
          >
            <Form
              actions={
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => navigate('/signin')}>
                    I already have an account
                  </Button>
                  <Button
                    variant="primary"
                    formAction="submit"
                    loading={submitting}
                    disabled={!canSubmit || submitting}
                  >
                    {isLeadCapture ? 'Send request' : 'Create tenant'}
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="m">
                {error ? <Alert type="error">{error}</Alert> : null}
                <FormField
                  label="Deployment"
                  description="Same platform, same API. Switch later with one command."
                >
                  <SegmentedControl
                    selectedId={deploy}
                    options={[
                      { id: 'cloud', text: 'Cloud (hosted by us)' },
                      { id: 'onprem', text: 'On-premises (your servers)' },
                    ]}
                    onChange={({ detail }) => {
                      const next = detail.selectedId as 'cloud' | 'onprem';
                      setDeploy(next);
                      // Reset plan to a sensible default for the new deploy mode.
                      setPlan(next === 'cloud' ? 'free' : 'starter');
                    }}
                  />
                </FormField>
                <Box color="text-body-secondary" fontSize="body-s">
                  Plan: <strong>{PLAN_LABELS[plan] ?? plan}</strong>
                </Box>
                {isLeadCapture ? (
                  <Alert type="info">
                    This plan is set up by our team. Fill in your company details and
                    we&apos;ll be in touch within 24 hours to walk you through installation
                    — no payment or password needed yet.
                  </Alert>
                ) : null}
                <FormField label="Company name">
                  <Input
                    value={companyName}
                    autoFocus
                    onChange={({ detail }) => setCompanyName(detail.value)}
                  />
                </FormField>
                <FormField
                  label="Tenant slug"
                  description="Lowercase letters, digits, hyphens. 3–32 chars, must start with a letter. Used in every VRN issued for your tenant."
                  errorText={
                    tenantTouched && tenant && !tenantValid
                      ? 'Tenant slug must match ^[a-z][a-z0-9-]{2,31}$'
                      : undefined
                  }
                >
                  <Input
                    value={tenant}
                    onChange={({ detail }) => {
                      setTenant(detail.value.toLowerCase());
                      setTenantTouched(true);
                    }}
                  />
                </FormField>
                <FormField label="Root username" description="Login name for the tenant root user.">
                  <Input value={username} onChange={({ detail }) => setUsername(detail.value)} />
                </FormField>
                <FormField label="Email">
                  <Input
                    value={email}
                    type="email"
                    onChange={({ detail }) => setEmail(detail.value)}
                  />
                </FormField>
                <FormField label="Display name (optional)">
                  <Input
                    value={displayName}
                    onChange={({ detail }) => setDisplayName(detail.value)}
                  />
                </FormField>
                {isLeadCapture ? null : (
                  <>
                    <FormField
                      label="Password"
                      errorText={
                        password && !passwordValid ? 'Must be at least 8 characters' : undefined
                      }
                    >
                      <Input
                        value={password}
                        type="password"
                        onChange={({ detail }) => setPassword(detail.value)}
                      />
                    </FormField>
                    <FormField
                      label="Confirm password"
                      errorText={
                        confirmPassword && !passwordsMatch ? 'Passwords do not match' : undefined
                      }
                    >
                      <Input
                        value={confirmPassword}
                        type="password"
                        onChange={({ detail }) => setConfirmPassword(detail.value)}
                      />
                    </FormField>
                  </>
                )}
                <Box color="text-body-secondary" fontSize="body-s">
                  By creating a tenant you agree to the Vextura{' '}
                  <Link href="/terms" external>
                    Terms of Service
                  </Link>
                  .
                </Box>
              </SpaceBetween>
            </Form>
          </form>
        </Container>
      </div>
    </Box>
  );
}
